import type {
  FinplanContribution, FinplanRow, FinplanSettings, FinplanYearOverride, CategoryKind,
} from './types.ts';

// ---------- periods ----------
/** the owner's part of a plan line: other people's planned shares are not the period's money */
export function ownAmount(line: { amount: number; shares?: { amount: number; covered?: number }[] }) {
  // a share the owner paid for the person himself is his part again
  return Math.max(0, line.amount - (line.shares ?? []).reduce((s, x) => s + x.amount - (x.covered ?? 0), 0));
}

/**
 * Whether a new transfer spends the period's money when the owner did not say: money leaving an
 * ordinary base-currency wallet for anything but savings/investments is spent. Reserve holds never are.
 */
export function transferInPeriod(from: { type: string; currency: string }, to: { type: string }, base: string, reserve = false) {
  const keeps = (type: string) => type === 'savings' || type === 'investment';
  return !reserve && from.currency === base && !keeps(from.type) && !keeps(to.type);
}

// ---------- dates ----------

export function toIso(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, '0');
  const day = String(d.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export function parseIso(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d));
}

export function addDays(iso: string, days: number): string {
  const d = parseIso(iso);
  d.setUTCDate(d.getUTCDate() + days);
  return toIso(d);
}

export function todayIso(tz = 'Europe/Moscow'): string {
  // en-CA formats as YYYY-MM-DD
  return new Intl.DateTimeFormat('en-CA', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date());
}

/**
 * Budget period that contains `date`, given the paydays (e.g. [10, 25]).
 * Returns the start date, end date (inclusive) and the slot index of the start day.
 */
export function periodFor(date: string, startDays: number[]): { start: string; end: string; slot: number } {
  const days = [...new Set(startDays)].sort((a, b) => a - b);
  const d = parseIso(date);
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  const day = d.getUTCDate();

  // candidate starts: this month's days <= day, otherwise last day of previous month
  let startY = y, startM = m, startDay: number;
  const le = days.filter((x) => x <= day);
  if (le.length) {
    startDay = le[le.length - 1];
  } else {
    startDay = days[days.length - 1];
    startM -= 1;
    if (startM < 0) { startM = 11; startY -= 1; }
  }
  const start = toIso(new Date(Date.UTC(startY, startM, startDay)));

  const idx = days.indexOf(startDay);
  let nextY = startY, nextM = startM, nextDay: number;
  if (idx < days.length - 1) {
    nextDay = days[idx + 1];
  } else {
    nextDay = days[0];
    nextM += 1;
    if (nextM > 11) { nextM = 0; nextY += 1; }
  }
  const end = addDays(toIso(new Date(Date.UTC(nextY, nextM, nextDay))), -1);
  return { start, end, slot: startDays.indexOf(startDay) };
}

/** «Период 25-9» style title */
export function periodTitle(start: string, end: string): string {
  return `${parseIso(start).getUTCDate()}-${parseIso(end).getUTCDate()}`;
}

// ---------- money ----------

/** sign applied to the absolute amount entered for a category kind */
export function kindSign(kind: CategoryKind): 1 | -1 {
  return kind === 'expense' || kind === 'repayment' || kind === 'loan' ? -1 : 1;
}

export const currencySymbols: Record<string, string> = { RUB: '₽', KZT: '₸', USD: '$', EUR: '€', USDT: '₮' };

export function formatMoney(value: number | null | undefined, currency = 'RUB', opts: { decimals?: number; sign?: boolean } = {}): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  const cents = Math.round(Math.abs(value) * 100);
  // kopecks are shown whenever there are any; whole sums stay without «,00»
  const decimals = opts.decimals ?? (cents % 100 !== 0 ? 2 : 0);
  // thousands are split by a narrow no-break space: a plain one is a whole digit wide in Roboto Mono
  const s = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: decimals, maximumFractionDigits: decimals }).format(Math.abs(value))
    .replace(/[  ]/g, ' ');
  const sign = cents === 0 ? '' : value < 0 ? '−' : opts.sign && value > 0 ? '+' : '';
  const sym = currencySymbols[currency] ?? currency;
  return currency === 'USD' ? `${sign}${sym}${s}` : `${sign}${s} ${sym}`;
}

export function round2(v: number): number {
  return Math.round(v * 100) / 100;
}

// ---------- long-term plan (compound interest) ----------

/**
 * Reproduces the «Финансовый план» sheet:
 * - years ≤ baseYear: capital grows only by the actual monthly contributions, no interest;
 * - later years: income grows by incomeGrowth, expenses by inflation (compounded from baseYear),
 *   12 × monthly delta is invested, the capital at the start of the year earns investRate and
 *   the money invested during the year earns half a year of interest on average.
 * Departures from the sheet: one-time expenses come off the same year's total; the base year, when it
 * is the current one, adds the months still ahead to the facts; passive income defaults to the real
 * rate (investRate − inflation), which can be spent without shrinking the capital in today's money.
 */
/**
 * What really happened in the years up to the base year: average monthly income and spending from
 * operations, and today's value of the investments. The capital of a past year is today's value minus
 * what was put in after that year, so the actual curve ends exactly at the real balance; the gap between
 * that and the contributions is the year's result (gains or losses). `yearEnds` are the real values on
 * December 31 of finished years; where one is known it replaces the walk back, so each year keeps its own
 * gains instead of the first year collecting them all. `monthsLeft` is how many months of the base year
 * are still ahead (0 once it is over).
 *
 * The broker's own figures for a year (`marketValue`, `marketResult` of its override) win over the
 * operations: the tracker only sees contributions, the broker sees market gains, dividends and coupons.
 * The value becomes the year's capital, the result its «Доходность»; with only the value the result is
 * what it takes to reach it.
 */
export interface FinplanActuals {
  capitalNow?: number | null;
  yearEnds?: { year: number; capital: number }[];
  monthsLeft?: number;
  years?: { year: number; months: number; income: number; expenses: number }[];
}

export function computeFinplan(
  s: FinplanSettings,
  contributions: FinplanContribution[],
  overrides: FinplanYearOverride[],
  actuals: FinplanActuals = {},
): FinplanRow[] {
  const byYear = new Map<number, number>();
  for (const c of contributions) byYear.set(c.year, (byYear.get(c.year) ?? 0) + c.amount);
  const ov = new Map(overrides.map((o) => [o.year, o]));
  const flows = new Map((actuals.years ?? []).filter((y) => y.months > 0).map((y) => [y.year, y]));
  const anchored = actuals.capitalNow !== null && actuals.capitalNow !== undefined;
  const yearEnds = new Map((actuals.yearEnds ?? []).map((y) => [y.year, y.capital]));
  const market = (year: number) => ov.get(year)?.marketValue ?? null;
  // value at the end of an actual year: the broker's value, its December 31 balance, else walked back from today's balance
  const capitalAt = (year: number) => {
    const known = market(year) ?? yearEnds.get(year);
    if (known !== undefined) return known;
    let v = actuals.capitalNow ?? 0;
    for (let y = year + 1; y <= s.baseYear; y++) v -= byYear.get(y) ?? 0;
    return v;
  };

  const rows: FinplanRow[] = [];
  let prev = s.firstYear <= s.baseYear ? 0 : s.startCapital;
  for (let year = s.firstYear; year <= s.lastYear; year++) {
    const k = Math.max(0, year - s.baseYear);
    const isActual = year <= s.baseYear;
    const flow = isActual ? flows.get(year) : undefined;
    const monthlyIncome = flow ? flow.income / flow.months : s.monthlyIncome * (1 + s.incomeGrowth) ** k;
    const monthlyExpenses = flow ? flow.expenses / flow.months : s.monthlyExpenses * (1 + s.inflation) ** k;
    const monthlyDelta = monthlyIncome - monthlyExpenses;
    const o = ov.get(year);
    const oneTimeExpenses = o?.oneTimeExpenses ?? 0;
    const contributed = byYear.get(year) ?? 0;
    // an actual year's result is the broker's, else whatever makes it end at its real value
    const marketValue = isActual ? market(year) : null;
    const result = !isActual ? 0
      : o?.marketResult ?? (anchored || marketValue !== null ? capitalAt(year) - prev - contributed + oneTimeExpenses : 0);
    // the rest of the current year: the planned delta is invested, the capital works for those months
    const forecastMonths = year === s.baseYear ? Math.min(12, Math.max(0, actuals.monthsLeft ?? 0)) : 0;
    const share = forecastMonths / 12;
    const ahead = isActual ? (s.monthlyIncome - s.monthlyExpenses) * forecastMonths : monthlyDelta * 12;
    const accumulated = isActual ? contributed + ahead : ahead;
    const capitalSoFar = isActual ? marketValue ?? prev + contributed + result - oneTimeExpenses : prev;
    const interestOnPrev = isActual ? capitalSoFar * s.investRate * share : prev * s.investRate;
    const interestOnCurrent = isActual ? (ahead * s.investRate * share) / 2 : (accumulated * s.investRate) / 2;
    const totalCapital = isActual ? capitalSoFar + ahead + interestOnPrev + interestOnCurrent
      : prev + accumulated + interestOnPrev + interestOnCurrent - oneTimeExpenses;
    const growth = result + interestOnPrev + interestOnCurrent;
    // on average the capital of the start of the year and half of the year's contributions were at work
    const working = prev + accumulated / 2;
    const passiveRate = o?.passiveRate ?? s.investRate - s.inflation;
    const passiveYear = totalCapital * passiveRate;
    rows.push({
      year,
      age: s.ageInFirstYear + (year - s.firstYear),
      isActual,
      forecastMonths,
      monthlyIncome,
      monthlyExpenses,
      monthlyDelta,
      accumulated,
      prevCapital: prev,
      interestOnPrev,
      interestOnCurrent,
      result,
      growth,
      growthRate: working > 0 ? growth / working : null,
      oneTimeExpenses,
      totalCapital,
      // in today's money: deflated to the base (current) year
      realCapital: totalCapital / (1 + s.inflation) ** Math.max(0, year - s.baseYear),
      passiveRate,
      passiveYear,
      passiveMonth: passiveYear / 12,
    });
    prev = totalCapital;
  }
  return rows;
}

// ---------- period forecast ----------

/**
 * Where the period is heading, like Money Lover's budget detail: the recommended daily amount,
 * the actual daily average of unplanned spending so far and the free money left on payday if that
 * pace continues. `unplannedSpent` is spending outside plan lines from the first day through today;
 * `remainder` already has it subtracted.
 */
export function periodForecast(input: { start: string; end: string; today: string; remainder: number; unplannedSpent: number }): {
  daysLeft: number; recommendedPerDay: number | null; actualPerDay: number; projectedRemainder: number;
} | null {
  const { start, end, today, remainder, unplannedSpent } = input;
  if (today < start || today > end) return null;
  const dayMs = 86400000;
  const elapsed = Math.round((parseIso(today).getTime() - parseIso(start).getTime()) / dayMs) + 1;
  const daysLeft = Math.round((parseIso(end).getTime() - parseIso(today).getTime()) / dayMs) + 1;
  const actualPerDay = round2(Math.max(0, unplannedSpent) / elapsed);
  // today is still running: the rest of it and every later day are spent at the average pace
  return {
    daysLeft,
    recommendedPerDay: remainder > 0 ? round2(remainder / daysLeft) : null,
    actualPerDay,
    projectedRemainder: round2(remainder - actualPerDay * (daysLeft - 1)),
  };
}

// ---------- comparing a stretch of days with the months before it ----------

const DAY_MS = 86400000;

/** days from `from` to `to` inclusive */
export function daysBetween(from: string, to: string): number {
  return Math.round((parseIso(to).getTime() - parseIso(from).getTime()) / DAY_MS) + 1;
}

/** the same day `by` months away; a day the month does not have becomes its last day (31 Oct − 1 → 30 Sep) */
export function addMonths(iso: string, by: number): string {
  const d = parseIso(iso);
  const y = d.getUTCFullYear(), m = d.getUTCMonth() + by, day = d.getUTCDate();
  const last = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  return toIso(new Date(Date.UTC(y, m, Math.min(day, last))));
}

/** the same stretch of dates `by` months away: a whole month stays a whole month, 10–24 stays 10–24 */
export function shiftRange(from: string, to: string, by: number): { from: string; to: string } {
  const f = addMonths(from, by);
  const wholeMonths = from.endsWith('-01') && addDays(to, 1).endsWith('-01');
  const t = wholeMonths ? addDays(addMonths(addDays(to, 1), by), -1) : addMonths(to, by);
  return { from: f, to: t < f ? f : t };
}

/**
 * What a report compares with, as Money Lover's «Сравнение ежемесячных отчетов»: up to a month long — the same
 * stretch one month earlier (this month to date against last month to the same day); longer — the equal
 * stretch right before it. Never overlaps the range itself.
 */
export function previousRange(from: string, to: string): { from: string; to: string } {
  const len = daysBetween(from, to);
  if (len > 31) return { from: addDays(from, -len), to: addDays(from, -1) };
  const r = shiftRange(from, to, -1);
  return r.to >= from ? { from: r.from, to: addDays(from, -1) } : r;
}

export interface PaceDay { date: string; spent: number; total: number }
export interface SpendingPace {
  days: PaceDay[];
  /** average cumulative spending of the same stretch of the previous months, per day of the range; null without history */
  baseline: number[] | null;
  baselineRanges: { from: string; to: string }[];
}

/**
 * Money Lover's «Compared to 3-Month Avg»: spending of the range day by day, cumulative, against the average
 * cumulative spending of the same stretch in each of the `months` months before. `daily` holds spending per date,
 * positive numbers, covering the earlier stretches too.
 */
export function spendingPace(from: string, to: string, daily: Record<string, number>, months = 3): SpendingPace {
  const len = Math.max(0, daysBetween(from, to));
  const days: PaceDay[] = [];
  let total = 0;
  for (let i = 0; i < len; i++) {
    const date = addDays(from, i);
    const spent = daily[date] ?? 0;
    total = round2(total + spent);
    days.push({ date, spent, total });
  }
  const ranges = Array.from({ length: months }, (_, k) => shiftRange(from, to, -(k + 1)));
  const windows = ranges.map((r) => {
    const cum: number[] = [];
    let t = 0;
    for (let i = 0; i < daysBetween(r.from, r.to); i++) { t += daily[addDays(r.from, i)] ?? 0; cum.push(t); }
    return cum;
  });
  const history = windows.some((w) => w.length > 0 && w[w.length - 1] > 0);
  // a shorter earlier month (February against March) keeps its final total for the remaining days
  const baseline = history ? days.map((_, i) => round2(windows.reduce((s, w) => s + (w[Math.min(i, w.length - 1)] ?? 0), 0) / windows.length)) : null;
  return { days, baseline, baselineRanges: ranges };
}
